import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useConfirm } from './Confirm';

export interface GroupSummary {
  name: string;
  students: number;
}

/**
 * Which class the register is showing, and what can be done to it.
 *
 * A centre teaches classes, so the register opens on all of them and narrows
 * to one. Renaming and dissolving live here rather than on a screen of their
 * own: a group is only ever a name on the students in it, and the place to
 * change that name is the place where you are looking at them.
 *
 * `undefined` means everybody, `null` means the students in no class at all —
 * the three enrolled one at a time who were never put anywhere.
 */
export function GroupBar({
  groups,
  ungrouped,
  selected,
  onSelect,
  onChanged,
}: {
  groups: GroupSummary[];
  ungrouped: number;
  selected: string | null | undefined;
  onSelect: (group: string | null | undefined) => void;
  /** Something moved: reload the register and the classes. */
  onChanged: (notice: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  const total = groups.reduce((sum, g) => sum + g.students, 0) + ungrouped;
  const current = typeof selected === 'string' ? groups.find((g) => g.name === selected) : undefined;

  async function rename(event: FormEvent) {
    event.preventDefault();
    if (!current || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<{ moved: number; name: string }>('/users/groups/rename', {
        from: current.name,
        to: name.trim(),
      });
      setRenaming(false);
      setName('');
      onSelect(data.name);
      onChanged(`“${current.name}” is now “${data.name}”.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not rename that class'));
    } finally {
      setBusy(false);
    }
  }

  async function dissolve() {
    if (!current) return;
    const yes = await ask({
      title: `Dissolve “${current.name}”?`,
      body: (
        <>
          <p>
            The {current.students} student{current.students === 1 ? '' : 's'} in it stay enrolled and
            keep everything they have sat. They simply belong to no class afterwards.
          </p>
          <p>Nobody is removed from the platform.</p>
        </>
      ),
      confirmLabel: 'Dissolve class',
      tone: 'danger',
    });
    if (!yes) return;

    setBusy(true);
    setError(null);
    try {
      await api.post('/users/groups/dissolve', { name: current.name });
      onSelect(undefined);
      onChanged(`“${current.name}” was dissolved. Its students are still enrolled.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not dissolve that class'));
    } finally {
      setBusy(false);
    }
  }

  if (groups.length === 0 && ungrouped === total) {
    // No class has ever been named here. Nothing to choose between, so the bar
    // stays out of the way until the first student is enrolled into one.
    return null;
  }

  return (
    <div className="groupbar">
      {dialog}
      <div className="groupbar__chips">
        <span className="eyebrow">Classes</span>
        <button
          type="button"
          className={`chip ${selected === undefined ? 'chip--on' : ''}`}
          onClick={() => onSelect(undefined)}
        >
          Everyone <span className="mono">{total}</span>
        </button>
        {groups.map((group) => (
          <button
            key={group.name}
            type="button"
            className={`chip ${selected === group.name ? 'chip--on' : ''}`}
            onClick={() => onSelect(group.name)}
          >
            {group.name} <span className="mono">{group.students}</span>
          </button>
        ))}
        {ungrouped > 0 && (
          <button
            type="button"
            className={`chip ${selected === null ? 'chip--on' : ''}`}
            onClick={() => onSelect(null)}
          >
            No class <span className="mono">{ungrouped}</span>
          </button>
        )}
      </div>

      {current && !renaming && (
        <div className="row">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setName(current.name);
              setRenaming(true);
            }}
          >
            Rename
          </button>
          <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void dissolve()}>
            Dissolve
          </button>
        </div>
      )}

      {current && renaming && (
        <form className="row" onSubmit={(e) => void rename(e)}>
          <input
            className="field__input"
            style={{ maxWidth: '18rem' }}
            value={name}
            autoFocus
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            aria-label={`New name for ${current.name}`}
          />
          <button className="btn btn--primary" type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Renaming…' : 'Rename'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setRenaming(false);
              setError(null);
            }}
          >
            Cancel
          </button>
        </form>
      )}

      {error && <p className="field__error">{error}</p>}
    </div>
  );
}
